import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { X } from 'lucide-react';

/**
 * iter67 — Profile completion modal (DISMISSIBLE).
 *
 * Renders ONLY when the current user's `profile_completed != true`.
 * Triggered automatically by Dashboard mount + after Google sign-in
 * lands the user on a protected page.
 *
 * iter67 change — the modal is now DISMISSIBLE:
 *   - X button in the top-right corner
 *   - "Skip for now" button beside the submit button
 *   - Esc key closes it
 *   - Backdrop click closes it
 *
 * To prevent the gate from immediately re-opening on the next render
 * pass, we remember the user's dismissal in sessionStorage scoped to
 * the current user_id. It will re-prompt:
 *   - On the next login (sessionStorage clears per browser-session)
 *   - When the user clicks the "Complete profile →" reminder banner
 *     on the Dashboard (which sets a query param the gate listens to)
 */
const DISMISS_KEY = 'myleaf.profile.dismissed_for';

function _isDismissedFor(userId) {
  try { return sessionStorage.getItem(DISMISS_KEY) === userId; }
  catch { return false; }
}
function _setDismissedFor(userId) {
  try { sessionStorage.setItem(DISMISS_KEY, userId); } catch { /* noop */ }
}
function _clearDismissed() {
  try { sessionStorage.removeItem(DISMISS_KEY); } catch { /* noop */ }
}

export default function CompleteProfileGate() {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '',
    address_line1: '', address_line2: '',
    city: '', state: '', postal_code: '', country: 'US',
  });

  // Re-open the modal when the user navigates with ?complete_profile=1
  // in the URL (Dashboard's reminder banner uses this). Listening on
  // `location.search` means React Router Link clicks reopen the gate
  // without a full page reload.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get('complete_profile') === '1') {
      _clearDismissed();
      setOpen(true);
      // Strip the param so refresh doesn't keep re-opening it after
      // the user dismisses again.
      sp.delete('complete_profile');
      const qs = sp.toString();
      const url = location.pathname + (qs ? `?${qs}` : '');
      window.history.replaceState({}, '', url);
    }
  }, [location.search, location.pathname]);

  // Fetch the canonical profile so we never act on stale `useAuth()` data.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get('/users/me/profile').then(({ data }) => {
      if (!alive) return;
      if (data?.profile_completed) {
        setOpen(false);
        _clearDismissed();
        return;
      }
      // If the user already dismissed this session, stay closed.
      if (_isDismissedFor(user.user_id)) {
        setOpen(false);
        return;
      }
      // Prefill from whatever Google gave us.
      const fullName = (data?.name || '').trim();
      const [fn, ...rest] = fullName.split(' ');
      setForm((f) => ({
        ...f,
        first_name: data.first_name || fn || '',
        last_name: data.last_name || (rest.join(' ') || ''),
        phone: data.phone || '',
        address_line1: data.address_line1 || '',
        address_line2: data.address_line2 || '',
        city: data.city || '',
        state: data.state || '',
        postal_code: data.postal_code || '',
        country: data.country || 'US',
      }));
      setOpen(true);
    }).catch(() => {});
    return () => { alive = false; };
  }, [user]);

  // Esc to dismiss.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismiss = () => {
    if (user?.user_id) _setDismissedFor(user.user_id);
    setOpen(false);
    toast('You can complete this anytime from your dashboard.', {
      duration: 4500,
    });
  };

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.phone.trim()) {
      toast.error('First name, last name, and phone number are required.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/users/me/profile', form);
      if (data?.user) setUser(data.user);
      _clearDismissed();
      toast.success('Profile saved.');
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="complete-profile-gate"
      onClick={(e) => {
        // Backdrop click → dismiss. Clicks inside the card don't bubble.
        if (e.target === e.currentTarget) dismiss();
      }}
    >
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--primary)/0.2)] rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto relative">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close — complete profile later"
          title="Close — you can finish this later from your dashboard"
          data-testid="profile-close-btn"
          className="absolute top-4 right-4 p-2 rounded-full text-[hsl(var(--primary)/0.6)] hover:text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.06)] transition-colors"
        >
          <X size={18} strokeWidth={2} />
        </button>
        <div className="p-7 border-b border-[hsl(var(--primary)/0.12)]">
          <p className="ml-overline">One last step</p>
          <h2 className="font-serif text-3xl text-[hsl(var(--primary))] mt-2">Complete your admin profile</h2>
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-3 max-w-lg">
            We need a few details so we can keep accurate records of who owns each memorial, ship plaques to the right place, and reach you if there's a question about an order. You can finish this later — just click <span className="font-semibold">Complete profile</span> on your dashboard.
          </p>
        </div>
        <form onSubmit={submit} className="p-7 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="First name *" testid="profile-first-name">
              <input className="ml-input" value={form.first_name}
                     onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                     data-testid="profile-first-name-input" required />
            </Field>
            <Field label="Last name *" testid="profile-last-name">
              <input className="ml-input" value={form.last_name}
                     onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                     data-testid="profile-last-name-input" required />
            </Field>
            <Field label="Phone number *" testid="profile-phone">
              <input className="ml-input" type="tel" value={form.phone}
                     placeholder="e.g. (555) 123-4567"
                     onChange={(e) => setForm({ ...form, phone: e.target.value })}
                     data-testid="profile-phone-input" required />
            </Field>
            <Field label="Email" testid="profile-email">
              <input className="ml-input" type="email" value={user?.email || ''} disabled data-testid="profile-email-input" />
            </Field>
            <Field label="Address" testid="profile-address1" wide>
              <input className="ml-input" value={form.address_line1}
                     placeholder="Street address"
                     onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                     data-testid="profile-address1-input" />
            </Field>
            <Field label="Address line 2" testid="profile-address2" wide>
              <input className="ml-input" value={form.address_line2}
                     placeholder="Apt / Suite (optional)"
                     onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
                     data-testid="profile-address2-input" />
            </Field>
            <Field label="City" testid="profile-city">
              <input className="ml-input" value={form.city}
                     onChange={(e) => setForm({ ...form, city: e.target.value })}
                     data-testid="profile-city-input" />
            </Field>
            <Field label="State / Region" testid="profile-state">
              <input className="ml-input" value={form.state}
                     onChange={(e) => setForm({ ...form, state: e.target.value })}
                     data-testid="profile-state-input" />
            </Field>
            <Field label="ZIP / Postal code" testid="profile-postal">
              <input className="ml-input" value={form.postal_code}
                     onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                     data-testid="profile-postal-input" />
            </Field>
            <Field label="Country" testid="profile-country">
              <input className="ml-input" value={form.country}
                     onChange={(e) => setForm({ ...form, country: e.target.value })}
                     data-testid="profile-country-input" />
            </Field>
          </div>
          <p className="text-[11px] text-[hsl(var(--primary)/0.6)]">
            * required. Your address is private — used only for plaque shipping
            and admin verification, never shown publicly.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-between gap-3">
            <button
              type="button"
              onClick={dismiss}
              data-testid="profile-skip-btn"
              className="text-[12px] tracking-[0.18em] uppercase font-semibold text-[hsl(var(--primary)/0.65)] hover:text-[hsl(var(--primary))] transition-colors"
            >
              Skip for now
            </button>
            <button type="submit" disabled={submitting} className="ml-btn-primary" data-testid="profile-submit-btn">
              {submitting ? 'Saving…' : 'Save and continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <label className={`flex flex-col gap-1.5 ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="ml-overline text-[10px]">{label}</span>
      {children}
    </label>
  );
}
